import React, { useState, useEffect } from 'react';

/**
 * VCPage — /vc
 * Public investor request page. Two intents share one endpoint:
 *   - access        → request a live-platform test login (manually granted)
 *   - conversation  → request a call
 * Arrives from /investors CTAs. ?intent=conversation pre-selects that form.
 * Matches the site restyle: #5b9bd5 wrapper, Georgia serif, #1e3a5f ink, white cards.
 * POSTs to /api/v1/vc-request (leads.py).
 */

const ink = '#1e3a5f';
const serif = "'Georgia', 'Times New Roman', serif";

const S = {
  page: { background: '#5b9bd5', minHeight: '100vh', fontFamily: serif, color: ink, padding: '0 0 80px' },
  container: { maxWidth: '760px', margin: '0 auto', padding: '0 24px' },
  hero: { textAlign: 'center', padding: '64px 0 32px' },
  h1: { fontSize: 'clamp(30px, 5vw, 44px)', fontWeight: 700, margin: '0 0 14px', lineHeight: 1.12 },
  sub: { fontSize: 'clamp(16px, 2.2vw, 19px)', maxWidth: '560px', margin: '0 auto', color: '#22344f', lineHeight: 1.5 },
  toggle: { display: 'flex', gap: '8px', justifyContent: 'center', margin: '28px auto 0', maxWidth: '520px' },
  tab: (active) => ({
    flex: 1, padding: '13px 16px', borderRadius: '10px', border: 'none', cursor: 'pointer',
    fontFamily: serif, fontSize: '15px', fontWeight: 700,
    background: active ? ink : 'rgba(255,255,255,0.75)', color: active ? '#F0F4F8' : ink,
    transition: 'all .15s',
  }),
  card: { background: 'rgba(255,255,255,0.97)', borderRadius: '14px', padding: '36px 40px', marginTop: '24px', boxShadow: '0 2px 14px rgba(30,58,95,0.14)' },
  intro: { fontSize: '15px', color: '#3a4a63', marginBottom: '22px', lineHeight: 1.5 },
  label: { display: 'block', fontSize: '13px', fontWeight: 700, marginBottom: '6px', marginTop: '16px', letterSpacing: '0.02em' },
  input: { width: '100%', padding: '11px 13px', borderRadius: '9px', border: '1px solid #cbd6e6', fontFamily: serif, fontSize: '15px', color: ink, boxSizing: 'border-box', background: '#fff' },
  textarea: { width: '100%', padding: '11px 13px', borderRadius: '9px', border: '1px solid #cbd6e6', fontFamily: serif, fontSize: '15px', color: ink, boxSizing: 'border-box', minHeight: '92px', resize: 'vertical', background: '#fff' },
  submit: { marginTop: '24px', width: '100%', padding: '14px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: ink, color: '#F0F4F8', fontFamily: serif, fontSize: '16px', fontWeight: 700 },
  note: { textAlign: 'center', fontSize: '13px', color: '#22344f', marginTop: '18px' },
  ok: { textAlign: 'center', padding: '20px 0' },
  okH: { fontSize: '22px', fontWeight: 700, marginBottom: '10px' },
  err: { color: '#b91c1c', fontSize: '14px', marginTop: '12px', textAlign: 'center' },
};

const useQueryIntent = () => {
  const [intent, setIntent] = useState('access');
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('intent');
    if (p === 'conversation') setIntent('conversation');
  }, []);
  return [intent, setIntent];
};

export default function VCPage() {
  const [intent, setIntent] = useQueryIntent();
  const [form, setForm] = useState({ contact_name: '', firm: '', role: '', email: '', aum_or_stage: '', message: '' });
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.email || !form.email.includes('@')) { setState('error'); return; }
    setState('sending');
    try {
      const res = await fetch(window.location.origin + '/api/v1/vc-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, intent }),
      });
      const data = await res.json();
      if (res.ok && data.ok) { setState('done'); } else { setState('error'); }
    } catch (e) { setState('error'); }
  };

  const isConv = intent === 'conversation';

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.hero}>
          <h1 style={S.h1}>Evaluate FHO Directly</h1>
          <p style={S.sub}>
            No deck-gating. Request a live login to test the platform yourself, or set up a
            conversation with the founder. We review every request personally.
          </p>
          <div style={S.toggle}>
            <button style={S.tab(!isConv)} onClick={() => setState('idle') || setIntent('access')}>Request platform access</button>
            <button style={S.tab(isConv)} onClick={() => setState('idle') || setIntent('conversation')}>Request a conversation</button>
          </div>
        </div>

        <div style={S.card}>
          {state === 'done' ? (
            <div style={S.ok}>
              <div style={S.okH}>Request received.</div>
              <p style={S.intro}>
                {isConv
                  ? "Thanks — we'll reach out to schedule a time."
                  : "Thanks — we review access requests personally and will follow up with a test login."}
                {' '}For anything urgent, write <strong>partnerships@freehappyone.com</strong>.
              </p>
            </div>
          ) : (
            <>
              <p style={S.intro}>
                {isConv
                  ? 'Tell us who you are and what you\u2019d like to discuss. We\u2019ll follow up to find a time.'
                  : 'Tell us a bit about you and we\u2019ll set you up with a live login to test the coaches directly.'}
              </p>

              <label style={S.label}>Name</label>
              <input style={S.input} value={form.contact_name} onChange={set('contact_name')} placeholder="Your name" />

              <label style={S.label}>Firm</label>
              <input style={S.input} value={form.firm} onChange={set('firm')} placeholder="Fund / firm" />

              <label style={S.label}>Role</label>
              <input style={S.input} value={form.role} onChange={set('role')} placeholder="Partner, Analyst, etc." />

              <label style={S.label}>Email *</label>
              <input style={S.input} value={form.email} onChange={set('email')} placeholder="you@firm.com" type="email" />

              {!isConv && (
                <>
                  <label style={S.label}>Fund stage / focus</label>
                  <input style={S.input} value={form.aum_or_stage} onChange={set('aum_or_stage')} placeholder="Pre-seed, seed, health/wellness focus, etc." />
                </>
              )}

              <label style={S.label}>{isConv ? 'What would you like to discuss? Availability?' : 'Anything you want us to know'}</label>
              <textarea style={S.textarea} value={form.message} onChange={set('message')}
                placeholder={isConv ? 'Topics, and a few times that work for you' : 'What you\u2019re evaluating for'} />

              <button style={S.submit} onClick={submit} disabled={state === 'sending'}>
                {state === 'sending' ? 'Sending\u2026' : (isConv ? 'Request a conversation' : 'Request platform access')}
              </button>

              {state === 'error' && (
                <div style={S.err}>
                  Something went wrong. Please check your email address, or write partnerships@freehappyone.com.
                </div>
              )}

              <p style={S.note}>We review every request personally. Your details are never shared.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
